import { useState, useEffect, useRef } from 'react';
import { themeI18n } from '../utils/i18n_helper.js';
import { fastSimonToShopifyProduct } from '../utils/dataProvider';
import { processFilteredFacets } from '../utils/metaobjects.js';
import { ProductCard } from './ProductCard.js';
import { Filters } from './Filters.js';
import { PromoTile } from './PromoTile.js';
import { Drawer } from './Drawer.js';
import { fetchShopifyVariantInventory } from '../utils/storefrontAPIVariantInventory.js';

import Icons from './Icons.js';
import { multilingual_url } from '../utils/multilingual-url.js';

const listingData = JSON.parse(document.querySelector('[data-product-listing-data]').innerHTML);
const url = new URL(window.location.href);

const narrowByInitial = [];
url.searchParams.forEach((value, key) => {
  if (key.startsWith('filter.')) {
    const paramName = key.substring('filter.'.length);
    narrowByInitial.push([paramName, value]);
  }
});

const validSortOptions = [
  // "reviews", // Keeping in case they add a review app
  "relevancy",
  "a_to_z",
  "z_to_a",
  "price_min_to_max",
  "price_max_to_min",
  "creation_date_oldest",
  "creation_date",
  "popularity",
];

function separateIntoBatches(array, batchSize) {
  const batch = [];
  for (let index = 0; index < array.length; index += batchSize) {
    batch.push(array.slice(index, index + batchSize));
  }
  return batch;
}

// Preload images for the first 8 products to improve LCP
function preloadProductImages(products, maxCount = 8) {
  const productsToPreload = products.slice(0, maxCount);
  
  productsToPreload.forEach((product) => {
    const shopifyProduct = fastSimonToShopifyProduct(product);
    
    if (!shopifyProduct?.featured_image) {
      return;
    }
    
    // Check if preload link already exists to avoid duplicates
    const imageUrl = shopifyProduct.featured_image + "&width=500";
    const existingPreload = document.querySelector(`link[rel="preload"][href="${imageUrl}"]`);
    
    if (!existingPreload) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = imageUrl;
      
      // Add responsive image hints for better preloading
      // Generate srcset for common breakpoints
      const widths = [165, 360, 533, 720, 940, 1066];
      const srcset = widths
        .map(width => `${shopifyProduct.featured_image}&width=${width} ${width}w`)
        .join(', ');
      
      if (srcset) {
        link.imageSrcset = srcset;
        // Typical sizes for product grid (adjust based on your layout)
        link.imageSizes = '(min-width: 1200px) calc((100vw - 10rem) / 4), (min-width: 990px) calc((100vw - 8rem) / 3), (min-width: 750px) calc((100vw - 3rem) / 2), 100vw';
      }
      
      document.head.appendChild(link);
    }
  });
}

export const ProductListing = ({ skeletonContent }) => {
  const [products, setProducts] = useState([]);
  const [results, setResults] = useState();
  const [sortBy, setSortBy] = useState(url.searchParams.get("sort_by") || 'relevancy');
  const [narrowBy, setNarrowBy] = useState(narrowByInitial);
  const [facets, setFacets] = useState([]);
  const [page, setPage] = useState(Number(url.searchParams.get("page")) || 1);
  const [prevPage, setPrevPage] = useState((Number(url.searchParams.get("page")) || 1) - 1);
  const [nextPage, setNextPage] = useState((Number(url.searchParams.get("page")) || 1) + 1);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [hiddenProducts, setHiddenProducts] = useState(0);
  // const [showDesktopFilters, setShowDesktopFilters] = useState(listingData.showFiltersByDefault);

  const listingRef = useRef(null);

  useEffect(() => {
    fetchSmartProductListing(page, "replace");
  }, [sortBy, narrowBy]);

  const fetchSmartProductListing = async (page, action) => {
    const sdkFunction = listingData.collectionId ? "smartCollections" : "fullTextSearch";
    let receivedProductBatch = false;
    let receivedFacetsBatch = false;
    setLoading(true);

    try {
      window.FastSimonSDK[sdkFunction]({
        term: listingData.collectionId ? null : listingData.search_term,
        categoryID: listingData.collectionId ? listingData.collectionId : null,
        narrowBy: narrowBy,
        sortBy: sortBy,
        page: page,
        productsPerPage: listingData.productsPerPage,
        withAttributes: true,
        callback: async (response) => {
          console.log('response', response); // gonna keep this for debugging purposes for now

          setResults(response.payload);

          if (response.payload.isNoResults) {
            setProducts([]);

            return;
          }

          if (response.action === 'facets and products') {
            receivedFacetsBatch = true;
            setFacets(response.payload.facets);
          }

          // Process products if not already done
          if (!receivedProductBatch && response.payload.products) {
            receivedProductBatch = true;

            const variantIds = response.payload.products
              .map(item => fastSimonToShopifyProduct(item)?.selected_or_first_available_variant?.id)
              .filter(Boolean);

            // Products batched into max 100 (Shopify API nodes query limit)
            // Then fetch inventories for all batches
            const batches = separateIntoBatches(variantIds, 100);
            const inventoryMaps = await Promise.all(
              batches.map(batch => fetchShopifyVariantInventory(batch))
            );
            const combinedInventoryMap = Object.assign({}, ...inventoryMaps);

            // Filter out products that have no image or inventory less than 2
            const productsFiltered = response.payload.products.filter(product => {
              const shopifyProduct = fastSimonToShopifyProduct(product);

              if (!shopifyProduct || !shopifyProduct.selected_or_first_available_variant) {
                console.warn("Missing variant or product for:", product);
                return false;
              }

              const variantId = shopifyProduct.selected_or_first_available_variant.id.toString();
              const inventoryQty = combinedInventoryMap?.[variantId] ?? 0;
              const hasImage = !!shopifyProduct.featured_image;
              const hasInventory = inventoryQty > 1;

              return hasImage && hasInventory;
            });

            const hiddenProductsCount = response.payload.products.length - productsFiltered.length;
            setHiddenProducts((prevCount) => prevCount + hiddenProductsCount);

            // Preload images for first 8 products on initial load or filter change
            if (action === "replace") {
              preloadProductImages(productsFiltered, 8);
            }

            if (action === "prepend") {
              setProducts((prevProducts) => [...productsFiltered, ...prevProducts]);
            } else if (action == "replace") {
              setProducts(productsFiltered);
            } else {
              setProducts((prevProducts) => [...prevProducts, ...productsFiltered]);
            }
          }
        }
      });

      if (listingData.collectionId) {
        window.FastSimonSDK.event({
          eventName: window.FastSimonEventName.SmartCollectionPreformed,
          data: {
            categoryID: listingData.collectionId,
            narrowBy,
            sortBy,
            products: products?.map(p => p.id).toString(),
            rescount: results?.totalResults,
            pagenum: page
          }
        });
      } else if (listingData.search_term) {
        window.FastSimonSDK.event({
          eventName: window.FastSimonEventName.SearchPerformed,
          data: {
            query: listingData.search_term || "",
            narrowBy,
            sortBy
          }
        });
      }

    } catch (error) {
      console.error("Error in fetchSmartProductListing:", error);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setInitialLoad(false);
      }, 1000);
    }
  };


  const loadPrevious = (event, prevPage) => {
    event.preventDefault();

    // Remove page parameter only if page is 1 (for SEO - avoid duplicate URLs)
    // Keep ?page=2, ?page=3, etc. for proper navigation and bookmarking
    if (prevPage === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', prevPage);
    }
    window.history.pushState(null, null, url.toString());

    fetchSmartProductListing(prevPage, "prepend");

    setPage(prevPage);
    setPrevPage(prevPage - 1);
  }

  const loadMore = (event, nextPage) => {
    event.preventDefault();

    // Remove page parameter only if page is 1 (for SEO - avoid duplicate URLs)
    // Keep ?page=2, ?page=3, etc. for proper navigation and bookmarking
    if (nextPage === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', nextPage);
    }
    window.history.pushState(null, null, url.toString());

    fetchSmartProductListing(nextPage, "append");

    setPage(nextPage);
    setNextPage(nextPage + 1);
  }

  const totalAfterFiltering = results ? results.totalResults - hiddenProducts : 0;
  const adjustedTotal = Math.max(products.length, totalAfterFiltering);

  return (
    <>
      {!initialLoad && results ? (
        <>
          {(listingData.enableSort || listingData.enableFilters) && (
            <>
              <div className='product-listing__top-bar'>
                <button className='product-listing__filter-toggle-button button button--secondary button--full-width hide-desktop'
                  onClick={() => window.dispatchEvent(new CustomEvent('filters'))}
                >
                  <Icons icon='filter' size='medium' />

                  {listingData.enableSort ? themeI18n('collection.filters.title_with_sort') : themeI18n('collection.filters.title')}
                  {narrowBy.length > 0 ? ` (${narrowBy.length})` : ''}
                </button>

                {listingData.enableFilters && (
                  <div className='product-listing__top-bar-filters'>
                    <button className='product-listing__filter-toggle-button-desktop hide-desktop-down'
                      onClick={() => window.dispatchEvent(new CustomEvent('filters'))}
                    >
                      {themeI18n('collection.filters.all_filters')}{narrowBy.length > 0 ? ` (${narrowBy.length})` : ''}

                      <Icons icon='filter' size='medium' />
                    </button>

                    {(facets.find((facet)=> facet[0] =='Colour')) && (
                      <button className='product-listing__filter-toggle-button-desktop product-listing__filter-toggle-button-desktop--quick-filter hide-desktop-down'
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('filters'))
                          window.dispatchEvent(new CustomEvent('open-accordion', {detail: {name: facets.find((facet)=> facet[0] =='Colour')[2]}}))
                        }}
                      >
                        {facets.find((facet)=> facet[0] =='Colour')[2]}
                      </button>
                    )}

                    {facets.find((facet)=> facet[0] == 'Global.size') && (
                      <button className='product-listing__filter-toggle-button-desktop product-listing__filter-toggle-button-desktop--quick-filter hide-desktop-down'
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('filters'))
                          window.dispatchEvent(new CustomEvent('open-accordion', {detail: {name: facets.find((facet)=> facet[0] == 'Global.size')[2]}}))
                        }}
                      >
                        {facets.find((facet)=> facet[0] == 'Global.size')[2]}
                      </button>
                    )}
                  </div>
                )}

                {listingData.enableSort && (
                  <div className='product-listing__sort-by-desktop hide-desktop-down'>
                    <select
                      id='sort-by-mobile'
                      title={themeI18n('collection.sort_by_label')}
                      value={sortBy}
                      onChange={(event)=> {
                        url.searchParams.set('sort_by', event.target.value);
                        url.searchParams.set('page', 1);
                        window.history.pushState(null, null, url.toString());

                        setPage(1);
                        setSortBy(event.target.value);
                        setPrevPage(0);
                        setNextPage(2);
                      }}
                    >
                      {validSortOptions.map((option) => {
                        return (
                          <option key={option} value={option}>
                            {themeI18n(`shopify.collections.sorting_fs.${option}`)}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                )}
              </div>

              <Drawer openEvent="filters" headerContent={listingData.enableFilters ? themeI18n('collection.filters.title') : themeI18n('collection.filters.sort_title')} contentClass='product-listing__filter-drawer'>
                <div className='product-listing__filter-drawer-body'>
                  <Filters
                    facets={facets}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    narrowBy={narrowBy}
                    setNarrowBy={setNarrowBy}
                    setPage={setPage}
                    setPrevPage={setPrevPage}
                    setNextPage={setNextPage}
                    name='filters'
                  />
                </div>

                <div className='product-listing__filter-drawer-footer'>
                  <button className='button button--full-width' onClick={() => window.dispatchEvent(new CustomEvent('close_filters'))}>
                    {themeI18n('collection.filters.see_results', {count: results.totalResults})}
                  </button>
                </div>
              </Drawer>
            </>
          )}

          <div className='product-listing__main load-more-pagination' ref={listingRef}>
            {results && results.isNoResults ? (
              <p>{themeI18n('search.no_results_match_filters')}</p>
            ) : (
              <>
                {prevPage > 0 && (
                  <div className='load-more-pagination__button-wrapper'>
                    <a className='button button--secondary' href={listingData.collectionId ?  multilingual_url(prevPage === 1 ? `/collections/${listingData.collectionHandle}` : `/collections/${listingData.collectionHandle}?page=${prevPage}`) : multilingual_url(prevPage === 1 ? `/search?q=${listingData.search_term}` : `/search?q=${listingData.search_term}&page=${prevPage}`)} onClick={(event)=> loadPrevious(event, prevPage)}>
                      {themeI18n('general.pagination.load_previous')}
                    </a>
                  </div>
                )}

                <div className={loading ? "product-listing__item-grid loading": "product-listing__item-grid"}>
                  {products.map((product, index) => {
                    if (!product.promotile) {
                      const shopifyProduct = fastSimonToShopifyProduct(product);

                      return (
                        <ProductCard
                          product={shopifyProduct}
                          key={product.id}
                          collectionId={listingData.collectionId}
                          searchTerm={listingData.search_term}
                          position={index + 1}
                          isAboveFold={index < 8}
                        />
                      )
                    } else {
                      return (
                        <PromoTile key={product.id} promoTile={product} listingDisplayType={listingData.displayType} />
                      )
                    }
                  })}
                </div>

                <div className='load-more-pagination__button-wrapper'>
                  <div className='load-more-pagination__count'>
                    {themeI18n('general.pagination.showing', {current: products.length, total: adjustedTotal})}
                  </div>

                  {nextPage <= results.pageCount && (
                    <a className='button button--secondary' href={listingData.collectionId ? multilingual_url(nextPage === 1 ? `/collections/${listingData.collectionHandle}` : `/collections/${listingData.collectionHandle}?page=${nextPage}`) : multilingual_url(nextPage === 1 ? `/search?q=${listingData.search_term}` : `/search?q=${listingData.search_term}&page=${nextPage}`)} onClick={(event)=> loadMore(event, nextPage)}>
                      {themeI18n('general.pagination.load_more')}
                    </a>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <div dangerouslySetInnerHTML={{ __html: skeletonContent }} />
      )}
    </>
  )
}
